import React from 'react';
import { FiCheckCircle, FiXCircle, FiClock, FiHash } from 'react-icons/fi';
import './JobStatusCard.css';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/**
 * Displays a single bulk-upload job: job_id, status, progress_percent, total_count, success_count, failure_count, updated_at.
 * Used in BulkUpload (check status result) and JobHistory (list).
 */
function JobStatusCard({ job }) {
  if (!job) return null;

  const jobId = job.job_id || job.jobId || '—';
  const status = (job.status || '—').toLowerCase();
  const progress = typeof job.progress_percent === 'number'
    ? Math.min(100, Math.max(0, job.progress_percent))
    : null;
  const totalCount = typeof job.total_count === 'number' ? job.total_count : null;
  const successCount = typeof job.success_count === 'number' ? job.success_count : null;
  const failureCount = typeof job.failure_count === 'number' ? job.failure_count : null;
  const updatedAt = job.updated_at || job.created_at;

  return (
    <div className="job-status-card" role="article" aria-label={`Job ${jobId}, ${status}`}>
      <div className="job-status-card-header">
        <div className="job-status-card-id-wrap">
          <span className="job-status-card-id-label">
            <FiHash className="job-status-card-id-icon" aria-hidden />
            Job ID
          </span>
          <code className="job-status-card-id">{jobId}</code>
        </div>
        <div className="job-status-card-meta">
          <span className="job-status-card-meta-label">
            <FiClock className="job-status-card-meta-icon" aria-hidden />
            Updated
          </span>
          <time className="job-status-card-meta-value" dateTime={updatedAt || undefined}>
            {formatDate(updatedAt)}
          </time>
        </div>
      </div>

      <div className="job-status-card-status-row">
        <span className="job-status-card-badge" data-status={status}>
          {job.status || '—'}
        </span>
      </div>

      {progress !== null && (
        <div className="job-status-card-progress">
          <div className="job-status-card-progress-header">
            <span className="job-status-card-progress-label">Progress</span>
            <span className="job-status-card-progress-pct" aria-hidden>
              {progress}%
            </span>
          </div>
          <div className="job-status-card-progress-track" aria-hidden>
            <div
              className="job-status-card-progress-bar"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${progress} percent complete`}
            />
          </div>
        </div>
      )}

      {(totalCount !== null || successCount !== null || failureCount !== null) && (
        <div className="job-status-card-stats">
          {totalCount !== null && (
            <div className="job-status-card-stat job-status-card-stat-total">
              <span className="job-status-card-stat-value">{totalCount}</span>
              <span className="job-status-card-stat-label">Total</span>
            </div>
          )}
          {successCount !== null && (
            <div className="job-status-card-stat job-status-card-stat-success">
              <span className="job-status-card-stat-value-wrap">
                <FiCheckCircle className="job-status-card-stat-icon" aria-hidden />
                <span className="job-status-card-stat-value">{successCount}</span>
              </span>
              <span className="job-status-card-stat-label">Success</span>
            </div>
          )}
          {failureCount !== null && (
            <div className="job-status-card-stat job-status-card-stat-failure">
              <span className="job-status-card-stat-value-wrap">
                <FiXCircle className="job-status-card-stat-icon" aria-hidden />
                <span className="job-status-card-stat-value">{failureCount}</span>
              </span>
              <span className="job-status-card-stat-label">Failed</span>
            </div>
          )}
        </div>
      )}

      {job.message && (
        <p className="job-status-card-message">{job.message}</p>
      )}
    </div>
  );
}

export default JobStatusCard;
export { formatDate };
